import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Input,
  Label,
} from '@design-system';
import {
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { useAdvisorSearch } from '../hooks/use-advisor-search';
import { useDeleteAdvisorAccount } from '../hooks/use-delete-advisor-account';
import { getErrorMessage } from '../utils/get-error-message';

const searchSchema = z.object({
  advisorQuery: z.string().min(1, 'Advisor ID or Email is required'),
});

function AdvisorField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 border-b border-gray-100 py-2 last:border-b-0">
      <span className="shrink-0 text-sm font-medium text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 break-words text-sm text-gray-900">{value ?? 'N/A'}</span>
    </div>
  );
}

export function DeleteAccountForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryRef = useRef(searchParams.get('advisorId') ?? '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof searchSchema>>({
    resolver: zodResolver(searchSchema),
    defaultValues: { advisorQuery: initialQueryRef.current },
  });

  const {
    searchAdvisor,
    advisor,
    clientCount,
    isPending: isSearching,
    isError: isSearchError,
    error: searchError,
    reset: resetSearch,
  } = useAdvisorSearch();

  const {
    deleteAccount,
    isPending: isDeleting,
    isError: isDeleteError,
    error: deleteError,
    reset: resetDelete,
  } = useDeleteAdvisorAccount();

  useEffect(() => {
    if (initialQueryRef.current) {
      searchAdvisor(initialQueryRef.current);
    }
  }, [searchAdvisor]);

  const onSearchAdvisor = (data: z.infer<typeof searchSchema>) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('advisorId', data.advisorQuery);
        return next;
      },
      { replace: true },
    );
    searchAdvisor(data.advisorQuery);
  };

  const openDeleteDialog = () => {
    setAcknowledged(false);
    setConfirmEmail('');
    resetDelete();
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!advisor) return;
    try {
      const result = await deleteAccount(advisor.advisorId);
      setDialogOpen(false);
      toast.success(
        `Advisor ${result.advisorId} deleted successfully (${result.deletedClientCount} clients removed).`,
      );
      resetSearch();
    } catch {
      // error state handled by hook
    }
  };

  const confirmTarget = advisor?.advisorEmail || advisor?.advisorId || '';
  const confirmMatch =
    confirmTarget && confirmEmail.trim().toLowerCase() === confirmTarget.toLowerCase();
  const canDelete = acknowledged && confirmMatch && !isDeleting;

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <form
        onSubmit={handleSubmit(onSearchAdvisor)}
        className="space-y-4"
      >
        <FormInput
          label="Advisor ID or Email"
          id="advisorQuery"
          type="text"
          placeholder="Enter advisor ID or email address"
          error={errors.advisorQuery}
          register={register('advisorQuery')}
          required={true}
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Search Advisor"
            isLoading={isSearching}
          />
        </PrimarySecondaryButtonContainer>

        {isSearchError && (
          <FormErrorAlert
            mainErrorMessage={getErrorMessage(searchError, 'Advisor not found')}
            className="mt-4"
          />
        )}
      </form>

      {/* Advisor Record Display */}
      {advisor && (
        <Card className="space-y-4 p-6">
          <div className="space-y-1 border-b pb-4">
            <h3 className="text-lg font-medium">Advisor Record</h3>
            <div className="flex items-center gap-2">
              {clientCount > 0 ? (
                <Badge variant="destructive">
                  {clientCount} client{clientCount !== 1 ? 's' : ''} will also be deleted
                </Badge>
              ) : (
                <Badge variant="secondary">No clients</Badge>
              )}
            </div>
          </div>

          <div className="space-y-0">
            <AdvisorField
              label="Advisor ID"
              value={advisor.advisorId}
            />
            <AdvisorField
              label="Email"
              value={advisor.advisorEmail}
            />
            <AdvisorField
              label="First Name"
              value={advisor.advisorFirstName}
            />
            <AdvisorField
              label="Last Name"
              value={advisor.advisorLastName}
            />
            <AdvisorField
              label="Organization Name"
              value={advisor.organizationName}
            />
            <AdvisorField
              label="Organization Display Name"
              value={advisor.organizationDisplayName}
            />
            <AdvisorField
              label="Phone Number"
              value={advisor.advisorPhoneNumber}
            />
            <AdvisorField
              label="Business Phone Number"
              value={advisor.advisorBusinessPhoneNumber}
            />
            <AdvisorField
              label="Scheduling Link URL"
              value={advisor.schedulingLinkUrl}
            />
            <AdvisorField
              label="Scheduling Link Display Text"
              value={advisor.schedulingLinkDisplayText}
            />
            <AdvisorField
              label="Send Client Results Link"
              value={String(advisor.sendClientResultsLinkSetting ?? false)}
            />
            <AdvisorField
              label="Account Verified"
              value={String(advisor.isAccountVerified ?? false)}
            />
            <AdvisorField
              label="Agreed to Terms of Service"
              value={String(advisor.hasAgreedToTermsOfService ?? false)}
            />
            <AdvisorField
              label="Auth0 User ID"
              value={advisor.auth0UserId}
            />
            <AdvisorField
              label="Cognito Ref"
              value={advisor.advisorCognitoRef}
            />
            <AdvisorField
              label="Feature Flags"
              value={
                advisor.featureFlags && Object.keys(advisor.featureFlags).length > 0 ? (
                  <code className="text-xs">{JSON.stringify(advisor.featureFlags)}</code>
                ) : (
                  'None'
                )
              }
            />
            <AdvisorField
              label="Created"
              value={
                advisor.advisorCreatedDateTime
                  ? new Date(advisor.advisorCreatedDateTime).toLocaleString()
                  : undefined
              }
            />
          </div>

          <div className="pt-4">
            <Button
              variant="destructive"
              onClick={openDeleteDialog}
              className="w-full"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Advisor Account
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={open => {
          if (!isDeleting) setDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md p-8">
          <div>
            <DialogTitle className="text-red-700">Delete Advisor Account</DialogTitle>
            <DialogDescription className="mt-1">
              This will permanently delete{' '}
              <strong>{advisor?.advisorEmail ?? advisor?.advisorId}</strong>
              {clientCount > 0 && (
                <> and <strong>{clientCount}</strong> associated client{clientCount !== 1 ? 's' : ''}</>
              )}
              . This action cannot be undone.
            </DialogDescription>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex items-start gap-2">
              <Checkbox
                id="acknowledge"
                checked={acknowledged}
                onCheckedChange={checked => setAcknowledged(checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="acknowledge"
                className="cursor-pointer text-sm leading-snug"
              >
                I understand this is permanent and cannot be undone
              </Label>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmEmail" className="text-sm">
                Type <strong>{confirmTarget}</strong> to confirm
              </Label>
              <Input
                id="confirmEmail"
                autoComplete="off"
                spellCheck={false}
                placeholder={confirmTarget}
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
              />
            </div>

            {isDeleteError && (
              <FormErrorAlert
                mainErrorMessage={getErrorMessage(deleteError, 'Failed to delete advisor account')}
              />
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canDelete}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
